// lib/divisionsFilter.js
//
// Welche Divisionen passen zu einem Wettkaempfer?
//
// Diese Frage wurde an zwei Stellen unabhaengig voneinander beantwortet --
// `components/registration/WettkampferAuswahlModal.jsx` (dort sogar mit DREI
// Abschriften derselben Tabellen in einer Datei) und
// `components/dashboard/WettkaempferDashboard.jsx`. Beide trugen denselben
// Fehler, und wer ihn an einer Stelle behob, haette ihn an der anderen
// stehengelassen. Ab hier gibt es eine Quelle.
//
// ⚠️ Was hier falsch steht, verhindert Anmeldungen, ohne es zu sagen. Der
// Verein sieht nur eine leere Liste und keinen Grund.

// ── Disziplingruppen ────────────────────────────────────────────────────────
// Formen/SV: Alter und Gewicht muessen genau passen, das Koennen darf eine
// Stufe hoeher sein. Kampf: umgekehrt.
export const FORMEN_TYPES = [
  'Formen', 'Selbstverteidigung', 'Forms', 'Self Defense', 'Breaking', 'Bruchtest',
];
export const KAMPF_TYPES = [
  'Kumite', 'Sparring', 'Fighting', 'Combat', 'Grappling',
  'Kickboxen', 'Kickboxing', 'Pointfighting', 'Continous Fighting',
  'Continuous Fighting', 'Fullcontact', 'Full Contact', 'K1', 'K-1', 'Rumble',
];

export const istFormenTyp = (divType = '') =>
  FORMEN_TYPES.some((t) => divType.toLowerCase().includes(t.toLowerCase()));
export const istKampfTyp = (divType = '') =>
  KAMPF_TYPES.some((t) => divType.toLowerCase().includes(t.toLowerCase()));

// ── Koennen ─────────────────────────────────────────────────────────────────
//
// ⚠️ Die Spalte `Skill_Level` enthaelt ZWEI verschiedene Dinge, und das war die
// Ursache von zwei Fehlern, die niemandem auffielen, weil sie nur als leere
// Liste sichtbar wurden:
//
//  1. Echte STUFEN — Beginner, Intermediate, Advanced, Black Belts und die
//     drei Grappling-Stufen. Sie ordnen Koennen, und daran wird gefiltert.
//
//  2. KENNZEICHEN einer Disziplin, die mit Koennen nichts zu tun haben:
//     'Amateur' steht bei ALLEN 304 Schlagdivisionen (Pointfighting, K1,
//     Continous Fighting, Fullcontact), 'Kumite' bei allen 34 Kumite-
//     Divisionen, 'Breaking' beim Bruchtest. Sie sind keine Stufe, sondern
//     der Name der Sache.
//
// Behandelte man Gruppe 2 wie Gruppe 1, muss jede Stufe jedes Kennzeichen
// einzeln aufgezaehlt bekommen -- und genau das ging schief:
//
//   'black belt' fehlte 'Amateur'  → ein Schwarzgurt bekam KEINE EINZIGE
//                                    Schlagdivision angeboten. Gemessen am
//                                    16.09.2026: Beginner 93 Treffer,
//                                    Schwarzgurt 0.
//   'Kumite' stand in KEINER Stufe → Kumite war fuer niemanden waehlbar.
//
// Deshalb wird jetzt andersherum gedacht: gefiltert wird NUR an echten Stufen.
// Alles andere in der Spalte ist ein Kennzeichen und offen fuer jeden.
export const SKILL_STUFEN = new Set([
  'Beginner', 'Intermediate', 'Advanced', 'Black Belts',
  'Rookie Grappling', 'Advanced Grappling', 'Master Grappling',
  // Eine Endrunde ist eine Stufe: sie bleibt den oberen Stufen vorbehalten.
  'Grand Champion',
]);

/** Kampf/Grappling: das eigene Koennen, nicht hoeher. */
export const SKILL_MAP = {
  beginner:     ['Beginner', 'Rookie Grappling'],
  intermediate: ['Intermediate', 'Advanced Grappling'],
  advanced:     ['Advanced', 'Master Grappling'],
  'black belt': ['Black Belts', 'Advanced', 'Master Grappling', 'Grand Champion'],
};

/** Formen/SV: eine Stufe hoeher ist ausdruecklich erlaubt. */
export const SKILL_MAP_FORMEN = {
  beginner:     ['Beginner', 'Intermediate', 'Rookie Grappling'],
  intermediate: ['Intermediate', 'Advanced', 'Advanced Grappling'],
  advanced:     ['Advanced', 'Black Belts', 'Master Grappling'],
  'black belt': ['Black Belts', 'Advanced', 'Master Grappling', 'Grand Champion'],
};

/** Gurtfarbe → Koennen, falls kein Skill-Level gepflegt ist. */
export const GURTFARBE_SKILL = {
  'weiß': 'beginner', 'weiss': 'beginner', 'weiß/gelb': 'beginner',
  'gelb': 'beginner', 'orange': 'beginner', 'gelb/grün': 'beginner',
  'grün': 'intermediate', 'gruen': 'intermediate', 'grün/blau': 'intermediate',
  'blau': 'intermediate',
  'braun': 'advanced', 'rot/braun': 'advanced', 'blau/rot': 'advanced', 'rot': 'advanced',
  'rot/schwarz': 'black belt', 'schwarz': 'black belt', 'schwarzgurt': 'black belt',
};

/**
 * Darf jemand mit diesem Koennen in dieser Division starten?
 *
 * @param {string} divSkill  Wert aus `divlisttdaalle.Skill_Level`
 * @param {string} koennen   'beginner' | 'intermediate' | 'advanced' | 'black belt'
 * @param {boolean} istFormen  Formen/SV erlauben eine Stufe hoeher
 */
export function skillPasst(divSkill, koennen, istFormen) {
  const stufe = (divSkill || '').trim();

  // Leer, oder ein Kennzeichen statt einer Stufe: keine Einschraenkung.
  if (!stufe || !SKILL_STUFEN.has(stufe)) return true;

  // Kein gepflegtes Koennen: nicht aussperren, sondern zeigen. Eine leere
  // Liste ohne Grund ist das schlechtere Ergebnis.
  const schluessel = (koennen || '').toLowerCase();
  const erlaubt = (istFormen ? SKILL_MAP_FORMEN : SKILL_MAP)[schluessel];
  if (!erlaubt) return true;

  return erlaubt.includes(stufe);
}

// ── Alters- und Gewichtsklassen ─────────────────────────────────────────────
//
// Im Kampf darf jemand eine Klasse HOEHER starten. Das stand als Absicht im
// Kommentar; im Code stand etwas anderes:
//
//   alterPasstHoeher   = alter   < ageFrom && alter   >= (ageFrom - 5)   // "~eine Altersklasse"
//   gewichtPasstHoeher = gewicht < minWeight && gewicht >= (minWeight - 10) // "~eine Gewichtsklasse"
//
// Feste Fenster koennen "eine Klasse" nicht ausdruecken, weil die Klassen
// verschieden breit sind. Gemessen am 16.09.2026 an den echten Daten:
//
//   Continous Fighting, Male: 13-15 | 16-18 | 18-29 | 30-39 | 40-99
//   Ein 13-Jaehriger bekam 16-18 angeboten (eine Klasse hoeher, gewollt)
//   UND 18-29 (13 < 18 und 13 >= 18-5) -- zwei Klassen hoeher, gegen
//   Erwachsene bis 29 und bis 95-150 kg.
//
//   Gewicht, Frauen 40+: 61-65 | 66-70 | 71-150
//   Bei 65 kg war 71-150 erlaubt (65 >= 71-10) -- ebenfalls zwei Klassen.
//
// Alters- und Gewichtsklassen gibt es, damit niemand zu Schaden kommt. Deshalb
// wird die Leiter jetzt aus den vorhandenen Divisionen abgelesen, und erlaubt
// ist genau die NAECHSTE Sprosse -- nicht jede innerhalb eines geratenen
// Fensters.

const leiterCache = new WeakMap();

/**
 * Geschlecht auf einen einheitlichen Schluessel bringen.
 *
 * ⚠️ In `divlisttdaalle` steht dasselbe Geschlecht in mehreren Schreibweisen --
 * gemessen am 16.09.2026 beim Grappling: die Klassen 14-16 tragen `'Male '`
 * MIT ANHAENGENDEM LEERZEICHEN, die Klassen 18-99 `'Male'`. Ohne Vereinheit-
 * lichung entstehen daraus zwei getrennte Leitern, und in der Leiter ohne die
 * Jugendklassen ist 18 die erste Sprosse -- ein 13-Jaehriger bekam so doch
 * wieder die Erwachsenenklasse angeboten.
 */
export function geschlechtSchluessel(wert) {
  const g = String(wert || '').trim().toLowerCase();
  if (!g) return 'x';
  if (g === 'mixed' || g === 'all' || g === 'm/f' || g.includes('/')) return 'mixed';
  if (g === 'm' || g === 'male' || g === 'männlich' || g === 'maennlich') return 'm';
  if (g === 'f' || g === 'w' || g === 'female' || g === 'weiblich') return 'f';
  return g;
}

const schluesselAlter = (d) =>
  `${(d.Division_Type || '').trim().toLowerCase()}|${geschlechtSchluessel(d.Gender)}`;

// ⚠️ Gewichtsklassen gelten je Altersband: 13-15 hat 0-42/43-47/…, 18-29 hat
// 0-57/58-63/… Ohne das Altersband im Schluessel liefen die Leitern ineinander.
const schluesselGewicht = (d) =>
  `${schluesselAlter(d)}|${d.Age_from}|${d.Age_to}`;

function leiternBauen(divisionen) {
  const alter = new Map();
  const gewicht = new Map();
  for (const d of divisionen) {
    const von = parseInt(d.Age_from, 10);
    if (Number.isFinite(von)) {
      const k = schluesselAlter(d);
      if (!alter.has(k)) alter.set(k, new Set());
      alter.get(k).add(von);
    }
    const minG = parseFloat(d.Minimun_Weight);
    if (Number.isFinite(minG)) {
      const k = schluesselGewicht(d);
      if (!gewicht.has(k)) gewicht.set(k, new Set());
      gewicht.get(k).add(minG);
    }
  }
  const sortiert = (m) => new Map([...m].map(([k, v]) => [k, [...v].sort((a, b) => a - b)]));
  return { alter: sortiert(alter), gewicht: sortiert(gewicht) };
}

/** Leitern zu einer Divisionsliste, je Liste einmal gebaut. */
export function leiternFuer(divisionen) {
  if (!Array.isArray(divisionen)) return { alter: new Map(), gewicht: new Map() };
  let l = leiterCache.get(divisionen);
  if (!l) { l = leiternBauen(divisionen); leiterCache.set(divisionen, l); }
  return l;
}

/** Die naechste Sprosse oberhalb eines Wertes, oder null. */
function naechsteSprosse(leiter, wert) {
  for (const grenze of leiter || []) if (grenze > wert) return grenze;
  return null;
}

/**
 * Passt das Alter?
 *
 * @param {boolean} eineHoeher  Im Kampf: die naechste Klasse ist erlaubt.
 */
export function alterPasst(div, alter, alleDivisionen, eineHoeher) {
  const von = parseInt(div.Age_from, 10);
  const bis = parseInt(div.Age_to, 10);
  if (!Number.isFinite(von) || !Number.isFinite(bis)) return true;
  if (!Number.isFinite(alter)) return true;

  if (alter >= von && alter <= bis) return true;
  if (!eineHoeher) return false;

  // Genau die naechste Sprosse, keine weitere.
  return von === naechsteSprosse(leiternFuer(alleDivisionen).alter.get(schluesselAlter(div)), alter);
}

/**
 * Führt diese Division überhaupt eine Gewichtsklasse?
 *
 * ⚠️ „0 bis 9999 kg" ist KEINE Klasse, sondern die Aussage „alle Gewichte".
 * Genau so stehen die sechs Rumble-Divisionen in der Datenbank (Altersklassen
 * 4-12, ein Wettkampf fuer alle). Alle uebrigen Kampfdivisionen -- Grappling,
 * Pointfighting, Continous Fighting, K1, Fullcontact, Kumite, zusammen 433 --
 * fuehren echte Klassen.
 */
export function hatGewichtsklasse(div) {
  const min = parseFloat(div?.Minimun_Weight);
  const max = parseFloat(div?.Maximum_Weight);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
  return !(min <= 0 && max >= 999);
}

/**
 * Passt das Gewicht?
 *
 * ⚠️ OHNE gepflegtes Gewicht gibt es KEINE Kampfdivision mit Gewichtsklasse.
 * (Entscheidung des Veranstalters vom 16.09.2026.)
 *
 * Vorher wurde ohne Gewicht gar nicht gefiltert -- dem Verein wurden dann alle
 * Klassen angeboten, von „0-42 kg" bis „95-150 kg". Er musste raten, und bei
 * der Einwaage stellte sich heraus, dass es die falsche war: die Gruppe musste
 * neu gebaut werden.
 *
 * ⚠️ Formen, Selbstverteidigung und Bruchtest bleiben offen -- dort gibt es
 * keine Gewichtsklassen, und ein fehlendes Gewicht schadet nicht. Rumble
 * ebenfalls: die sechs Divisionen fuehren „0 bis 9999 kg", also keine Klasse.
 *
 * ⚠️ Die Maske muss SAGEN, warum nichts dasteht. Eine leere Liste ohne Grund
 * ist schlimmer als eine falsche Auswahl -- dafuer gibt es
 * `kampfGesperrtWegenGewicht`.
 */
export function gewichtPasst(div, gewicht, alleDivisionen, eineHoeher) {
  const min = parseFloat(div.Minimun_Weight);
  const max = parseFloat(div.Maximum_Weight);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return true;

  if (!Number.isFinite(gewicht) || gewicht <= 0) {
    // Kampf + echte Gewichtsklasse = gesperrt. Alles andere bleibt offen.
    return !(eineHoeher && hatGewichtsklasse(div));
  }

  if (gewicht >= min && gewicht <= max) return true;
  if (!eineHoeher) return false;

  return min === naechsteSprosse(leiternFuer(alleDivisionen).gewicht.get(schluesselGewicht(div)), gewicht);
}

/**
 * Passt das Geschlecht?
 *
 * ⚠️ Es gibt genau zwei (Entscheidung des Veranstalters vom 16.09.2026):
 * maennlich oder weiblich, zugeordnet wie bei der Geburt.
 *
 * ⚠️ FEHLT die Angabe, bleiben nur GESCHLECHTSOFFENE Divisionen (Male/Female,
 * mixed). Vorher wurde ohne Angabe gar nicht geprueft -- einer Frau waeren
 * dann Maennerdivisionen angeboten worden, und das faellt erst am
 * Wettkampftag auf. Der Abgleich mit der Dojosoftware kann einen Wettkaempfer
 * ohne Angabe anlegen; darum ist der Fall nicht bloss theoretisch.
 */
export function geschlechtPasst(div, geschlecht) {
  const d = geschlechtSchluessel(div?.Gender);
  // Die Division ist fuer alle offen.
  if (d === 'mixed' || d === 'x') return true;

  const w = geschlechtSchluessel(geschlecht);
  // Keine Angabe am Wettkaempfer: nur offene Divisionen, sonst nichts.
  if (w === 'x') return false;

  return w === d;
}

/** Fehlt die Angabe maennlich/weiblich? */
export function geschlechtFehlt(wettkaempfer) {
  return geschlechtSchluessel(wettkaempfer?.geschlecht) === 'x';
}

/**
 * Sind einem Wettkaempfer Kampfdisziplinen gesperrt, weil sein Gewicht fehlt?
 *
 * Damit beantwortet die Maske die Frage, die sonst offenbliebe: „warum steht
 * hier nichts?"
 */
export function kampfGesperrtWegenGewicht(wettkaempfer) {
  return !(parseFloat(wettkaempfer?.gewicht) > 0);
}
